import { useState, useMemo, useEffect } from "react";
import { C, EXERCISE_LIBRARY } from "../constants";
import { toDisplayWeight, weightLabel } from "../utils/units";
import { fmtTimer } from "../utils/dateHelpers";
import { Btn, StatCard, Badge, Modal, lblSt, iStyle } from "./ui";
import { getProgressionSuggestion } from "../utils/progression";
import { CustomExerciseModal } from "./CustomExerciseModal";
import { resolveExercise, lookupOverride, resolveProgressionDirection } from "../utils/exercises";

export const WorkoutRunner = ({
  plan, onFinish, onClose, settings, workouts,
  customExercises = [], userExerciseOverrides = {},
  onUpdateCustomExercise, onSetExerciseOverride,
}) => {
  const [editingExercise, setEditingExercise] = useState(null);
  const [exIdx,    setExIdx]    = useState(0);
  const [setIdx,   setSetIdx]   = useState(0);
  const [phase,    setPhase]    = useState("active");
  const [restEndTime, setRestEndTime] = useState(null);
  const [elapsed,  setElapsed]  = useState(0);
  const [done,     setDone]     = useState(false);
  const [exerciseOrder, setExerciseOrder] = useState(() => plan.exercises.map((_, i) => i));
  const [exerciseOverrides, setExerciseOverrides] = useState({});
  const [subModalOpen,    setSubModalOpen]    = useState(false);
  const [subTargetIndex,  setSubTargetIndex]  = useState(null);
  const [subQuery,        setSubQuery]        = useState("");

  // Compute progression suggestions per exercise — pure pass-through to the engine
  const suggestions = useMemo(() => {
    const map = {};
    plan.exercises.forEach((origEx, i) => {
      const ex = exerciseOverrides[i] || origEx;
      const repRange = ex.repRange ?? { min: Math.max(1, (ex.reps || 1) - 2), max: ex.reps || 1 };
      const override = lookupOverride(userExerciseOverrides, ex.exerciseId, ex.name);
      const opts = {
        workouts,
        exerciseId: ex.exerciseId,
        exerciseName: ex.name,
        repRange,
        unit: settings.weightUnit,
        progressionDirection: resolveProgressionDirection({ id: ex.exerciseId, name: ex.name }, customExercises),
      };
      if (override?.useCustomIncrement && override.increment != null) opts.increment = override.increment;
      const result = getProgressionSuggestion(opts);
      if (result) map[ex.name] = result;
    });
    return map;
  }, [plan, exerciseOverrides, workouts, settings, userExerciseOverrides]);

  const openEdit = (ex) => {
    const resolved = resolveExercise({ id: ex.exerciseId, name: ex.name }, customExercises, userExerciseOverrides);
    if (resolved) setEditingExercise(resolved);
  };

  const handleEditSave = (libEntry) => {
    if (!editingExercise) return;
    if (editingExercise.isCustom) {
      onUpdateCustomExercise?.(editingExercise, libEntry);
    } else {
      const ovId = editingExercise.id || (editingExercise.name || "").toLowerCase();
      onSetExerciseOverride?.(ovId, editingExercise.name, {
        useCustomIncrement: !!libEntry.useCustomIncrement,
        increment: libEntry.useCustomIncrement ? libEntry.increment : null,
      });
    }
  };

  const prevSessionMap = useMemo(() => {
    const byName = {};
    workouts.filter(w => w.type === "strength").forEach(w => {
      const key = w.name.toLowerCase();
      if (!byName[key]) byName[key] = [];
      byName[key].push(w);
    });
    const result = {};
    Object.entries(byName).forEach(([key, list]) => {
      list.sort((a, b) => b.date.localeCompare(a.date));
      const lastDate = list[0].date;
      const setsOnDay = list.filter(w => w.date === lastDate);
      result[key] = { date: lastDate, sets: setsOnDay.map(w => ({ reps: w.reps, weight: w.weight })) };
    });
    return result;
  }, [workouts]);

  const [log, setLog] = useState(() =>
    plan.exercises.map(ex => {
      const setCount = Math.max(1, ex.sets || 1);
      const repRange = ex.repRange;
      const sug      = suggestions[ex.name];
      const prev     = prevSessionMap[(ex.name || "").toLowerCase()];

      // Target reps: progression+midpoint when history exists, repRange.min otherwise; legacy ex.reps as fallback
      let targetReps;
      if (repRange && (sug || prev)) {
        targetReps = Math.round((repRange.min + repRange.max) / 2);
      } else if (repRange) {
        targetReps = repRange.min;
      } else if (ex.reps != null) {
        targetReps = ex.reps;
      } else {
        targetReps = 8;
      }

      // Target weight (display units): progression suggestion → previous session → legacy plan weight → empty
      let targetWeight = "";
      if (sug && sug.mode === "exact" && sug.weight > 0) {
        targetWeight = sug.weight;
      } else if (prev?.sets?.length) {
        const maxW = prev.sets.reduce((m, s) => s.weight > m ? s.weight : m, 0);
        if (maxW > 0) targetWeight = toDisplayWeight(maxW, settings.weightUnit);
      } else if (ex.weight != null && ex.weight > 0) {
        targetWeight = toDisplayWeight(ex.weight, settings.weightUnit);
      }

      const repsStr = String(targetReps);
      const wStr    = targetWeight === "" ? "" : String(targetWeight);
      const completedSets = Array.from({ length: setCount }, () => ({ reps: repsStr, weight: wStr }));
      // Pre-allocate one dropset slot for planned dropsets — empty values, filled on entry; attached to the final working set
      if (ex.intensityTechnique === "dropset") {
        completedSets.push({ reps: "", weight: "", isDropset: true, parentSetIndex: setCount - 1 });
      }
      // Pre-allocate a single starter mini set for planned myo-reps — additional mini sets are added dynamically.
      // myoRepTarget is the rep target per mini set, not a count.
      if (ex.intensityTechnique === "myo_reps") {
        const repTarget = String(ex.myoRepTarget ?? 3);
        completedSets.push({ reps: repTarget, weight: "", isMyoRep: true, parentSetIndex: setCount - 1 });
      }
      return { ...ex, sets: setCount, completedSets };
    })
  );

  useEffect(() => {
    const t = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const remainingSecs = restEndTime
    ? Math.max(0, Math.ceil((restEndTime - Date.now()) / 1000))
    : 0;

  useEffect(() => {
    if (phase === "rest" && restEndTime && remainingSecs <= 0) {
      setRestEndTime(null);
      setPhase("active");
    }
  }, [phase, restEndTime, remainingSecs]);

  const realIdx   = exerciseOrder[exIdx];
  const baseEx    = plan.exercises[realIdx];
  const ex        = exerciseOverrides[realIdx] || baseEx;
  const exLog     = log[realIdx];
  const totalSets = exLog.completedSets.length;

  // Resolve a runtime exercise (with substitution applied) given its position in exerciseOrder
  const resolveAt = (ei) => exerciseOverrides[exerciseOrder[ei]] || plan.exercises[exerciseOrder[ei]];

  // Group-aware label like "A1" / "A2" — index within group is by position in exerciseOrder
  const groupLabelAt = (ei) => {
    const e = resolveAt(ei);
    const g = e?.supersetGroup;
    if (!g) return null;
    let pos = 0;
    for (let i = 0; i <= ei; i++) {
      if ((resolveAt(i)?.supersetGroup || null) === g) pos++;
    }
    return `${g}${pos}`;
  };

  // Execution step list — flat sequence of (exIdx, setIdx) tuples accounting for supersets.
  // Group members are interleaved by set index; non-group exercises emit their sets sequentially.
  const steps = useMemo(() => {
    const out = [];
    const handled = new Set();
    exerciseOrder.forEach((rid, ei) => {
      if (handled.has(ei)) return;
      const e = exerciseOverrides[rid] || plan.exercises[rid];
      const group = e?.supersetGroup || null;
      if (!group) {
        const total = log[rid]?.completedSets.length || 0;
        for (let si = 0; si < total; si++) out.push({ exIdx: ei, setIdx: si });
        handled.add(ei);
        return;
      }
      const memberEis = [];
      exerciseOrder.forEach((rid2, i) => {
        const e2 = exerciseOverrides[rid2] || plan.exercises[rid2];
        if ((e2?.supersetGroup || null) === group) memberEis.push(i);
      });
      const maxSets = Math.max(0, ...memberEis.map(i => log[exerciseOrder[i]]?.completedSets.length || 0));
      for (let si = 0; si < maxSets; si++) {
        memberEis.forEach(i => {
          const ln = log[exerciseOrder[i]]?.completedSets.length || 0;
          if (si < ln) out.push({ exIdx: i, setIdx: si });
        });
      }
      memberEis.forEach(i => handled.add(i));
    });
    return out;
  }, [exerciseOrder, plan.exercises, exerciseOverrides, log]);

  const curStepIdx = steps.findIndex(s => s.exIdx === exIdx && s.setIdx === setIdx);
  const isLast     = curStepIdx === steps.length - 1;

  const advanceSet = () => {
    const next = steps[curStepIdx + 1];
    if (!next) { setDone(true); return; }

    const nextRealIdx   = exerciseOrder[next.exIdx];
    const nextSetEntry  = log[nextRealIdx]?.completedSets[next.setIdx];
    const nextIsDropset = !!nextSetEntry?.isDropset;
    const nextIsMyoRep  = !!nextSetEntry?.isMyoRep;
    const nextIsFollowUp = nextIsDropset || nextIsMyoRep;

    const curEx     = ex;
    const nextEx    = exerciseOverrides[nextRealIdx] || plan.exercises[nextRealIdx];
    const sameGroup = curEx?.supersetGroup && curEx.supersetGroup === nextEx?.supersetGroup;
    const sameSet   = next.setIdx === setIdx;
    const inRound   = sameGroup && sameSet; // alternating exercises within a superset round → no rest

    if (nextIsFollowUp) {
      // Auto-fill follow-up set based on the parent set. Dropsets reduce weight; myo-reps copy it.
      setLog(prev => {
        const upd = prev.map(e => ({ ...e, completedSets: e.completedSets.map(s => ({ ...s })) }));
        const cs = upd[nextRealIdx].completedSets[next.setIdx];
        if (cs.weight === "" || cs.weight == null) {
          const prevW = Number(prev[realIdx].completedSets[setIdx]?.weight) || 0;
          if (prevW > 0) {
            cs.weight = nextIsDropset
              ? String(Math.round(prevW * 0.8 * 2) / 2) // dropset: ~80% rounded to 0.5
              : String(prevW);                           // myo-rep: same weight as parent
          }
        }
        if (cs.reps === "" || cs.reps == null) {
          cs.reps = nextIsDropset
            ? (prev[realIdx].completedSets[setIdx]?.reps || "")
            : "3"; // myo-rep default mini target
        }
        return upd;
      });
    }

    setExIdx(next.exIdx);
    setSetIdx(next.setIdx);

    if (nextIsFollowUp || inRound) {
      setPhase("active");
      setRestEndTime(null);
    } else {
      setPhase("rest");
      setRestEndTime(Date.now() + (curEx?.restSecs || 0) * 1000);
    }
  };

  const addDropset = () => {
    setLog(prev => {
      const next = prev.map(e => ({ ...e, completedSets: e.completedSets.map(s => ({ ...s })) }));
      const sets = next[realIdx].completedSets;
      // Insert right after the current set, but past any follow-ups already attached to it,
      // so cascading runtime sets stay in the order they were added.
      let insertAt = setIdx + 1;
      while (insertAt < sets.length && (sets[insertAt].isDropset || sets[insertAt].isMyoRep) && sets[insertAt].parentSetIndex === setIdx) {
        insertAt++;
      }
      sets.forEach(s => {
        if (typeof s.parentSetIndex === "number" && s.parentSetIndex >= insertAt) {
          s.parentSetIndex += 1;
        }
      });
      sets.splice(insertAt, 0, { reps: "", weight: "", isDropset: true, parentSetIndex: setIdx });
      return next;
    });
  };

  const addMyoReps = () => {
    setLog(prev => {
      const next = prev.map(e => ({ ...e, completedSets: e.completedSets.map(s => ({ ...s })) }));
      const sets = next[realIdx].completedSets;
      const curSet = sets[setIdx];
      // If user is on a mini set, the new one joins the same cluster (sibling of the existing mini set).
      // Otherwise the current set becomes the activation parent for a new cluster.
      const newParent = curSet?.isMyoRep && typeof curSet.parentSetIndex === "number"
        ? curSet.parentSetIndex
        : setIdx;
      // Insert at the end of the cluster — past any existing follow-ups already attached to newParent.
      let insertAt = setIdx + 1;
      while (insertAt < sets.length && (sets[insertAt].isDropset || sets[insertAt].isMyoRep) && sets[insertAt].parentSetIndex === newParent) {
        insertAt++;
      }
      sets.forEach(s => {
        if (typeof s.parentSetIndex === "number" && s.parentSetIndex >= insertAt) {
          s.parentSetIndex += 1;
        }
      });
      const repTarget = String(ex.myoRepTarget ?? 3);
      sets.splice(insertAt, 0, { reps: repTarget, weight: "", isMyoRep: true, parentSetIndex: newParent });
      return next;
    });
  };

  const skipSet = () => {
    setLog(prev => {
      const next = prev.map(e => ({ ...e, completedSets: e.completedSets.map(s => ({ ...s })) }));
      next[realIdx].completedSets[setIdx].skipped = true;
      return next;
    });
    const next = steps[curStepIdx + 1];
    if (!next) { setDone(true); return; }
    setExIdx(next.exIdx);
    setSetIdx(next.setIdx);
  };

  const skipRest = () => { setPhase("active"); setRestEndTime(null); };

  const updateLog = (field, val) => {
    setLog(prev => {
      const next = prev.map(e => ({ ...e, completedSets: e.completedSets.map(s => ({ ...s })) }));
      next[realIdx].completedSets[setIdx][field] = val;
      return next;
    });
  };

  const fullLibrary = useMemo(() => [...EXERCISE_LIBRARY, ...customExercises], [customExercises]);

  const openSubstitute = () => {
    setSubTargetIndex(realIdx);
    setSubQuery("");
    setSubModalOpen(true);
  };

  const applySubstitute = (selected) => {
    if (subTargetIndex == null) return;
    const idx  = subTargetIndex;
    const base = plan.exercises[idx];
    const merged = {
      ...base,
      exerciseId: selected.id || null,
      name: selected.name,
      muscleGroup: selected.muscleGroup,
      type: selected.type,
      primarySubgroup:    selected.p || null,
      secondarySubgroups: selected.s || [],
    };
    setExerciseOverrides(prev => ({ ...prev, [idx]: merged }));
    setLog(prev => prev.map((entry, i) =>
      i === idx
        ? { ...entry, name: merged.name, muscleGroup: merged.muscleGroup, exerciseId: merged.exerciseId }
        : entry
    ));
    setSubModalOpen(false);
    setSubTargetIndex(null);
  };

  const subTargetEx = subTargetIndex != null
    ? (exerciseOverrides[subTargetIndex] || plan.exercises[subTargetIndex])
    : null;

  const suggestedSubs = useMemo(() => {
    if (!subTargetEx) return [];
    const curName = subTargetEx.name.toLowerCase();
    const curLib  = fullLibrary.find(e =>
      (subTargetEx.exerciseId && e.id === subTargetEx.exerciseId) ||
      e.name.toLowerCase() === curName
    );
    const curP = curLib?.p || subTargetEx.primarySubgroup    || null;
    const curS = curLib?.s || subTargetEx.secondarySubgroups || [];
    const curT = curLib?.t || [];
    const candidates = fullLibrary.filter(e => e.name.toLowerCase() !== curName);

    if (settings.advancedMode) {
      const sSet = new Set(curS);
      const tSet = new Set(curT);
      return candidates
        .map(e => {
          let score = 0;
          if (e.p && curP && e.p === curP) score += 3;
          score += 2 * (e.s || []).filter(x => sSet.has(x)).length;
          score += 1 * (e.t || []).filter(x => tSet.has(x)).length;
          return { ex: e, score };
        })
        .filter(x => x.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 8)
        .map(x => x.ex);
    }
    return candidates
      .filter(e => e.muscleGroup === subTargetEx.muscleGroup)
      .slice(0, 8);
  }, [subTargetEx, fullLibrary, settings.advancedMode]);

  const subFiltered = useMemo(() => {
    if (!subQuery.trim() || !subTargetEx) return [];
    const q = subQuery.toLowerCase();
    return fullLibrary
      .filter(e => e.name.toLowerCase() !== subTargetEx.name.toLowerCase() && e.name.toLowerCase().includes(q))
      .slice(0, 30);
  }, [subQuery, fullLibrary, subTargetEx]);

  const handleSkipAndReturn = () => {
    if (exIdx >= exerciseOrder.length - 1) return;
    setExerciseOrder(prev => {
      const newOrder = [...prev];
      const [skipped] = newOrder.splice(exIdx, 1);
      newOrder.splice(exIdx + 1, 0, skipped);
      return newOrder;
    });
    setSetIdx(0);
  };

  const restPct = ex.restSecs > 0 ? ((ex.restSecs - remainingSecs) / ex.restSecs) * 100 : 100;

  if (done) {
    const totalVol       = log.reduce((sum, e) => sum + e.completedSets.filter(cs => !cs.skipped).reduce((s, cs) => s + (Number(cs.reps) || 0) * (Number(cs.weight) || 1), 0), 0);
    const totalSetsCount = log.reduce((s, e) => s + e.completedSets.filter(cs => !cs.skipped).length, 0);
    const numericLog     = log.map((e, idx) => {
      const pos = exerciseOrder.indexOf(idx);
      return {
        ...e,
        completionOrder: pos >= 0 ? pos : idx,
        completedSets: e.completedSets.map(cs => ({
          ...cs,
          reps:   cs.reps   === "" ? 0 : Number(cs.reps),
          weight: cs.weight === "" ? 0 : Number(cs.weight),
        })),
      };
    });
    return (
      <div style={{ position: "fixed", inset: 0, background: C.bg, zIndex: 200, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 30 }}>
        <div style={{ fontSize: 70, marginBottom: 14 }}>🏆</div>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 40, fontWeight: 800, color: C.text, letterSpacing: "-0.02em", marginBottom: 4 }}>Workout done.</div>
        <div style={{ color: C.textDim, fontSize: 15, marginBottom: 36 }}>{plan.name} · {fmtTimer(elapsed)}</div>
        <div style={{ display: "flex", gap: 16, marginBottom: 40, flexWrap: "wrap", justifyContent: "center" }}>
          <StatCard label="Exercises" value={plan.exercises.length} />
          <StatCard label="Total Sets" value={totalSetsCount} />
          <StatCard label="Volume" value={`${(totalVol / 1000).toFixed(1)}k`} sub="lbs" accent />
        </div>
        <Btn variant="primary" onClick={() => onFinish(numericLog, elapsed)} style={{ padding: "14px 48px", fontSize: 17 }}>Save to History</Btn>
        <button onClick={onClose} style={{ marginTop: 14, background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 13 }}>Discard workout</button>
      </div>
    );
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: C.bg, zIndex: 200, display: "flex", flexDirection: "column", overflowY: "auto" }}>
      {/* Top bar */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: C.text }}>{plan.emoji} {plan.name}</div>
          <div style={{ color: C.muted, fontSize: 12 }}>Exercise {exIdx + 1}/{plan.exercises.length} · ⏱ {fmtTimer(elapsed)}</div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 22 }}>✕</button>
      </div>

      {/* Progress rail */}
      <div style={{ display: "flex", gap: 3, padding: "10px 20px 0", flexShrink: 0 }}>
        {plan.exercises.map((_, i) => (
          <div key={i} style={{ height: 3, flex: 1, borderRadius: 4, background: C.border, opacity: i === exIdx ? 1 : i < exIdx ? 1 : 0.4,
            ...(i < exIdx ? { background: C.accent } : {}) }} />
        ))}
      </div>

      <div style={{ flex: 1, padding: "16px 20px 40px", maxWidth: 520, margin: "0 auto", width: "100%" }}>
        {/* Exercise heading */}
        <div style={{ textAlign: "center", margin: "16px 0 22px" }}>
          {(() => {
            const label = groupLabelAt(exIdx);
            return label ? (
              <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 4 }}>
                Superset · {label}
              </div>
            ) : null;
          })()}
          <div
            onClick={() => openEdit(ex)}
            title="Edit exercise"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 800, color: C.text, lineHeight: 1.1, letterSpacing: "-0.02em", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            {ex.name}
            <span style={{ fontSize: 14, color: C.muted, opacity: 0.6 }}>✎</span>
          </div>
          <div style={{ marginTop: 8 }}><Badge>{ex.muscleGroup}</Badge></div>
        </div>

        {/* Set bubbles */}
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 26, flexWrap: "wrap" }}>
          {Array.from({ length: totalSets }, (_, i) => {
            const cs        = exLog.completedSets[i];
            const isSkipped = cs?.skipped;
            const isDropset = cs?.isDropset;
            const isMyoRep  = cs?.isMyoRep;
            const isFollowUp = isDropset || isMyoRep;
            const isDone    = i < setIdx && !isSkipped;
            const isCurrent = i === setIdx;
            const accent    = isDropset ? C.blue : isMyoRep ? C.orange : C.accent;
            const bubbleSize = isMyoRep ? 26 : isDropset ? 30 : 38;
            const radius     = isMyoRep ? 6 : isDropset ? 7 : 9;
            const fontSize   = isFollowUp ? 10 : 14;
            // Working-set number is the count of non-follow-ups up to and including this index
            const workingNum = isFollowUp ? null : exLog.completedSets.slice(0, i + 1).filter(s => !s.isDropset && !s.isMyoRep).length;
            const label = isSkipped ? "–" : isDone ? "✓" : isDropset ? "DS" : isMyoRep ? "M" : workingNum;
            return (
              <div key={i} style={{
                width: bubbleSize, height: bubbleSize, borderRadius: radius,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize, fontWeight: 700,
                background: isDone ? accent : isCurrent ? accent + "22" : C.surface,
                border: `1.5px solid ${isSkipped ? C.border : i <= setIdx ? accent : C.border}`,
                color: isDone ? "#1e1d1b" : isSkipped ? C.muted : isCurrent ? accent : C.muted,
                opacity: isSkipped ? 0.5 : 1,
                marginLeft: isFollowUp ? -2 : 0,
              }}>
                {label}
              </div>
            );
          })}
        </div>

        {phase === "rest" ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ color: C.muted, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>Rest Period</div>
            <div style={{ position: "relative", width: 150, height: 150, margin: "0 auto 24px" }}>
              <svg width="150" height="150" style={{ transform: "rotate(-90deg)" }}>
                <circle cx="75" cy="75" r="64" fill="none" stroke={C.border} strokeWidth="9" />
                <circle cx="75" cy="75" r="64" fill="none" stroke="#7aabd4" strokeWidth="9"
                  strokeDasharray={`${2 * Math.PI * 64}`}
                  strokeDashoffset={`${2 * Math.PI * 64 * (1 - restPct / 100)}`}
                  style={{ transition: "stroke-dashoffset 1s linear" }} />
              </svg>
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <div style={{ fontSize: 40, fontWeight: 800, color: C.text, lineHeight: 1 }}>{remainingSecs}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>seconds</div>
              </div>
            </div>
            <Btn onClick={skipRest} style={{ padding: "10px 36px", marginBottom: 14 }}>Skip Rest</Btn>
            <div style={{ color: C.muted, fontSize: 12 }}>
              Up next:{" "}
              {(() => {
                const next = steps[curStepIdx + 1];
                if (!next) return "Last set!";
                const nextEx = resolveAt(next.exIdx);
                const sameEx = next.exIdx === exIdx;
                const label  = groupLabelAt(next.exIdx);
                const prefix = label ? `${label} ` : "";
                return sameEx
                  ? `Set ${next.setIdx + 1} — ${prefix}${nextEx.name}`
                  : `${prefix}${nextEx.name}`;
              })()}
            </div>
          </div>
        ) : (
          <>
            {/* Previous session reference */}
            {prevSessionMap[ex.name.toLowerCase()] && (() => {
              const prev    = prevSessionMap[ex.name.toLowerCase()];
              const prevSet = prev.sets[setIdx] || prev.sets[prev.sets.length - 1];
              const dispW   = toDisplayWeight(prevSet.weight, settings.weightUnit);
              const wl      = weightLabel(settings.weightUnit);
              return (
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 16px", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ fontSize: 13, color: C.muted }}>Last session</div>
                    <div style={{ fontSize: 11, color: C.muted, background: C.card, padding: "2px 8px", borderRadius: 5 }}>
                      {new Date(prev.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </div>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.textDim }}>
                    {prevSet.weight > 0 ? `${dispW} ${wl}` : "Bodyweight"} × {prevSet.reps}
                  </div>
                </div>
              );
            })()}

            {/* Progression suggestion */}
            {(() => {
              const sug = suggestions[ex.name];
              if (!sug) return null;
              const wl = weightLabel(settings.weightUnit);
              const repRange = ex.repRange;
              const targetReps = repRange
                ? Math.round((repRange.min + repRange.max) / 2)
                : (ex.reps ?? null);
              let mainLine = null;
              let subLine  = null;
              if (sug.mode === "exact" && sug.weight > 0) {
                mainLine = targetReps != null
                  ? `Suggested: ${sug.weight} ${wl} × ${targetReps}`
                  : `Suggested: ${sug.weight} ${wl}`;
                subLine  = sug.repSuggestion;
              } else if (sug.mode === "exact") {
                mainLine = sug.repSuggestion;
              } else if (sug.mode === "suggest") {
                mainLine = sug.message;
              }
              if (!mainLine) return null;
              return (
                <div style={{ background: C.surface, border: `1px solid ${C.blue}44`, borderRadius: 10, padding: "10px 16px", marginBottom: 12 }}>
                  <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 4 }}>
                    Based on last session
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.blue }}>
                    {mainLine}
                  </div>
                  {subLine && (
                    <div style={{ fontSize: 12, color: C.textDim, marginTop: 2 }}>
                      {subLine}
                    </div>
                  )}
                </div>
              );
            })()}

            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "20px 22px", marginBottom: 18 }}>
              {(() => {
                const cur = exLog.completedSets[setIdx];
                const isDropset = cur?.isDropset;
                const isMyoRep  = cur?.isMyoRep;
                if (isDropset) {
                  return (
                    <div style={{ color: C.blue, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14 }}>
                      Dropset
                    </div>
                  );
                }
                if (isMyoRep) {
                  // Mini set position within this parent's cluster
                  const parent = cur.parentSetIndex;
                  const miniNum = exLog.completedSets.slice(0, setIdx + 1).filter(s => s.isMyoRep && s.parentSetIndex === parent).length;
                  return (
                    <div style={{ color: C.orange, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14 }}>
                      Mini Set {miniNum}
                    </div>
                  );
                }
                const workingCount = exLog.completedSets.filter(cs => !cs.isDropset && !cs.isMyoRep).length;
                const workingPos   = exLog.completedSets.slice(0, setIdx + 1).filter(cs => !cs.isDropset && !cs.isMyoRep).length;
                return (
                  <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14 }}>
                    Set {workingPos} of {workingCount}
                  </div>
                );
              })()}
              <div style={{ display: "flex", gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={lblSt}>Reps</label>
                  <input type="number" inputMode="numeric" value={exLog.completedSets[setIdx].reps}
                    onChange={e => updateLog("reps", e.target.value)}
                    style={{ ...iStyle, fontSize: 30, fontWeight: 700, textAlign: "center", padding: "12px 8px", color: C.text }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={lblSt}>Weight ({weightLabel(settings.weightUnit)})</label>
                  <input type="number" inputMode="decimal" value={exLog.completedSets[setIdx].weight}
                    onChange={e => updateLog("weight", e.target.value)}
                    style={{ ...iStyle, fontSize: 30, fontWeight: 800, textAlign: "center", padding: "12px 8px" }} />
                </div>
              </div>
            </div>

            <button onClick={advanceSet} style={{
              width: "100%", padding: "16px 0",
              background: isLast ? C.green : C.accent,
              color: "#1e1d1b", border: "none", borderRadius: 12, cursor: "pointer",
              fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 700, letterSpacing: "0.02em",
            }}>
              {isLast ? "Finish Workout" : "Complete Set →"}
            </button>

            {!isLast && (
              <button onClick={skipSet} style={{
                width: "100%", padding: "10px 0", marginTop: 8,
                background: "transparent", color: C.muted,
                border: `1px dashed ${C.border}`, borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 500,
              }}>Skip set</button>
            )}

            {exIdx < exerciseOrder.length - 1 && (
              <button onClick={handleSkipAndReturn} style={{
                width: "100%", padding: "10px 0", marginTop: 8,
                background: "transparent", color: C.muted,
                border: `1px dashed ${C.border}`, borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 500,
              }}>↻ Come back to this exercise</button>
            )}

            <button onClick={openSubstitute} style={{
              width: "100%", padding: "10px 0", marginTop: 8,
              background: "transparent", color: C.muted,
              border: `1px dashed ${C.border}`, borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 500,
            }}>⇄ Substitute exercise{exerciseOverrides[realIdx] ? " (substituted)" : ""}</button>

            <button onClick={addDropset} style={{
              width: "100%", padding: "10px 0", marginTop: 8,
              background: "transparent", color: C.blue,
              border: `1px dashed ${C.blue}55`, borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 600,
            }}>↓ + Dropset</button>

            {(() => {
              const cur = exLog.completedSets[setIdx];
              const peek = exLog.completedSets[setIdx + 1];
              const inCluster = cur?.isMyoRep
                || (peek?.isMyoRep && peek?.parentSetIndex === setIdx);
              const label = inCluster ? "＋ + Add Mini Set" : "＋ + Myo-Reps";
              return (
                <button onClick={addMyoReps} style={{
                  width: "100%", padding: "10px 0", marginTop: 8,
                  background: "transparent", color: C.orange,
                  border: `1px dashed ${C.orange}55`, borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 600,
                }}>{label}</button>
              );
            })()}

            {(setIdx > 0 || exIdx > 0) && (
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                {setIdx > 0 && <Btn onClick={() => setSetIdx(s => s - 1)} style={{ flex: 1 }}>← Prev Set</Btn>}
                {exIdx > 0  && <Btn onClick={() => { setExIdx(i => i - 1); setSetIdx(0); }} style={{ flex: 1 }}>← Prev Exercise</Btn>}
              </div>
            )}
          </>
        )}

        {/* Exercises overview */}
        <div style={{ marginTop: 34, borderTop: `1px solid ${C.border}`, paddingTop: 18 }}>
          <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>All Exercises</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {exerciseOrder.map((rIdx, i) => {
              const e = plan.exercises[rIdx];
              return (
                <div key={rIdx} onClick={() => openEdit(e)} style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "9px 12px",
                  background: i === exIdx ? C.accent + "0d" : C.surface,
                  border: `1px solid ${i === exIdx ? C.accent + "44" : C.border}`,
                  borderRadius: 8, cursor: "pointer",
                }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: 5, flexShrink: 0,
                    background: i < exIdx ? C.accent : i === exIdx ? C.accent + "22" : C.surface,
                    border: `1px solid ${i <= exIdx ? C.accent : C.border}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, color: i < exIdx ? "#1e1d1b" : C.muted, fontWeight: 700,
                  }}>
                    {i < exIdx ? "✓" : i + 1}
                  </div>
                  {(() => {
                    const gl = groupLabelAt(i);
                    return gl ? (
                      <span style={{ fontSize: 10, fontWeight: 700, color: C.blue, letterSpacing: "0.06em", marginRight: -2, minWidth: 18 }}>{gl}</span>
                    ) : null;
                  })()}
                  <span style={{ flex: 1, fontSize: 13, color: i === exIdx ? C.accent : i < exIdx ? C.textDim : C.text, fontWeight: i === exIdx ? 700 : 400 }}>{e.name}</span>
                  <span style={{ fontSize: 11, color: C.muted }}>{e.sets}×{e.repRange ? `${e.repRange.min}-${e.repRange.max}` : e.reps}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {editingExercise && (
        <CustomExerciseModal
          initial={editingExercise}
          lockMetadata={!editingExercise.isCustom}
          onSave={handleEditSave}
          onClose={() => setEditingExercise(null)}
        />
      )}

      {subModalOpen && subTargetEx && (
        <Modal onClose={() => setSubModalOpen(false)} title={`Substitute: ${subTargetEx.name}`} wide>
          <div style={{ marginBottom: 18 }}>
            <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
              Suggested {settings.advancedMode ? "(by muscle overlap)" : `(${subTargetEx.muscleGroup})`}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflowY: "auto" }}>
              {suggestedSubs.map(e => (
                <button key={`s:${e.id || e.name}`} onClick={() => applySubstitute(e)} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "9px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
                  background: C.card, color: C.text, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
                }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {e.name}
                    {e.p && <Badge>{e.p}</Badge>}
                  </span>
                  <Badge color="blue">{e.muscleGroup}</Badge>
                </button>
              ))}
              {suggestedSubs.length === 0 && (
                <div style={{ color: C.muted, fontSize: 12, textAlign: "center", padding: "16px 0" }}>
                  No similar exercises found
                </div>
              )}
            </div>
          </div>
          <div>
            <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
              All Exercises
            </div>
            <input value={subQuery} onChange={e => setSubQuery(e.target.value)}
              placeholder="Search…" autoFocus
              style={{ ...iStyle, marginBottom: 8 }} />
            {subQuery.trim() ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 240, overflowY: "auto" }}>
                {subFiltered.map(e => (
                  <button key={`f:${e.id || e.name}`} onClick={() => applySubstitute(e)} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "8px 12px", borderRadius: 7, border: `1px solid ${C.border}`,
                    background: C.card, color: C.text, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
                  }}>
                    <span>{e.name}</span>
                    <Badge color="blue">{e.muscleGroup}</Badge>
                  </button>
                ))}
                {subFiltered.length === 0 && (
                  <div style={{ color: C.muted, fontSize: 12, textAlign: "center", padding: "12px 0" }}>
                    No matches for "{subQuery}"
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: C.muted, fontSize: 12, textAlign: "center", padding: "12px 0" }}>
                Start typing to browse the full library
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};
